import React from 'react';
import PropTypes from 'prop-types';
import css from './ConfirmDialog.scss';
import Modal from 'react-modal';
import StyledButton from 'common/StyledButton';
import Icon from "../SF/MaterialIcon";
import FocusTrap from "focus-trap-react";

/**
 * Creates a modal that presents a header and text to the end user. The primary button is the confirm
 * button, while the optional close/cancel button allows to separate between "submitting" or simply closing
 * the dialog without making a change.
 *
 * To create a modal without separate behavior, simply skip providing an onClose function and onConfirm will
 * be used for the main button, as well as any other behavior used to close the modal (such as the Escape key).
 */
export default class ConfirmDialog extends React.Component {

  constructor(props) {
    super(props);
    this.mainButtonRef = null;

    // Reference: http://reactcommunity.org/react-modal/accessibility/#app-element
    Modal.setAppElement(document.getElementById(props.appElementId) || 'body');
  }

  get cancelButton() {
    // Do not render cancel button if no close function is passed
    if ( !this.props.onClose ) return null;
    return (
      <StyledButton
        className={css['cancel-button']}
        inverted
        onClick={this.props.onClose}>
        {this.props.cancelText}
      </StyledButton>
    );
  }

  // get classname depending on the props passed
  get modalClasses() {
    const classes = [ css['modal'] ];
    if (this.props.className) classes.push(this.props.className);
    return classes.join(' ');
  }

  render() {
    return (
      <Modal
        isOpen={this.props.isOpen}
        onRequestClose={this.props.onClose || this.props.onConfirm}
        className={this.modalClasses}
        onAfterOpen={() => {this.mainButtonRef && this.mainButtonRef.focus()}}
        overlayClassName={this.props.overlayClassName || css['overlay']}
        aria={{
          labelledby: "dialog-title",
          describedby: "dialog-description"
        }}>
        <FocusTrap active={this.props.focusTrap && this.props.isOpen} >
        <div className={`${css['content']}`}>
          <StyledButton
              className={css['close-button']}
              onClick={this.props.onClose || this.props.onConfirm}
              aria-label="Close"
              asLink>
            <Icon icon='close' />
          </StyledButton>
          <h1 id="dialog-title" className={css['header']}>{this.props.headerText}</h1>
          <div id="dialog-description" className={css['description']}>{this.props.descriptionText}</div>
          {this.props.body ? <div className={css['content-body']}>{this.props.body}</div> : null}
        </div>
        {/* Note that buttons can overflow based on their texts */}
        <div 
          className={css['actions']} 
          style={{justifyContent: !this.props.onClose ? 'center' : null}}>
          {this.cancelButton}
          <StyledButton
            className={this.props.confirmButtonClassName || css['confirm-button']}
            onClick={this.props.onConfirm}
            data-role='confirmSubmit'
            ref={inputRef => { this.mainButtonRef = inputRef}}>
            {this.props.confirmText}
          </StyledButton>
        </div>
        </FocusTrap>
      </Modal>
    );
  }
}

ConfirmDialog.defaultProps = {
  appElementId: 'main-center',
  cancelText: 'Cancel',
  confirmText: 'Confirm',
  descriptionText: 'Are you sure?',
  focusTrap: false,
  headerText: 'Confirm dialog',
};

ConfirmDialog.propTypes = {
  appElementId: PropTypes.string,
  body: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  cancelText: PropTypes.string,
  className: PropTypes.string,
  confirmButtonClassName: PropTypes.string,
  confirmText: PropTypes.string,
  descriptionText: PropTypes.string,
  focusTrap: PropTypes.bool,
  headerText: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func.isRequired,
  overlayClassName: PropTypes.string
};
